<template>
  <div id="add-form">
    <div class="form-disabled" v-if="formDisabled"></div>
    <a-modal
      cl
      title="Refresh Token"
      :visible="refreshModal"
      ok-text="Yes"
      cancel-text="No"
      @ok="connectToFB"
      @cancel="hideRefreshModal"
      :closable="false"
    >
      <p>
        Refreshing the token will remove the current authorization for the app to get leads from Facebook and utilize
        one from your account. <br /><br />
        If a token isn't found for your account for the page this form is on the token currently in place will remain in
        place.
        <br /><br />
        Do you want to replace the token?
      </p>
    </a-modal>

    <a-modal
      cl
      title="Refresh Successful"
      :visible="refreshOthersModal"
      ok-text="Yes"
      cancel-text="No"
      @ok="refreshOtherFormTokens"
      @cancel="hideRefreshOthersModal"
      :closable="false"
    >
      Successfully refreshed token. Would you like to refresh the other forms on the same page?
    </a-modal>

    <div class="title-bar">
      <a-icon type="arrow-left" @click="goBack" />
      <h1>Edit Form Mapping</h1>
      <a-button @click="showRefreshModal" id="refresh-button">
        Refresh
      </a-button>
    </div>
    <a-form v-if="!loading">
      <a-form-item label="Page" :label-col="{ span: 5 }" :wrapperCol="{ span: 13 }">
        <h2>{{ data.PageName }}</h2>
      </a-form-item>
      <a-form-item label="Form" :label-col="{ span: 5 }" :wrapperCol="{ span: 13 }">
        <h2>
          {{ data.FormName }}
          <span class="form-id">{{ data.FbFormId }}</span>
        </h2>
      </a-form-item>
      <a-form-item>
        <field-mappings
          :checking-form="checkingForm"
          :is-production="isProduction"
          :show-endpoint-switch="true"
          :send-to-regular-endpoint="sendToRegularEndpoint"
        ></field-mappings>
      </a-form-item>
      <a-form-item class="center-buttons" :wrapper-col="{ offset: 5, span: 13 }">
        <a-button
          :disabled="canSave"
          :loading="savingForm"
          class="create-button"
          type="primary"
          @click="submitMappings()"
        >
          {{ savingForm ? "Saving" : "Update Form Mapping" }}
        </a-button>
      </a-form-item>
    </a-form>
    <a-spin size="large" v-if="loading"></a-spin>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";
import FieldMappings from "@/components/FieldMappings";

export default {
  components: {
    FieldMappings,
  },
  computed: {
    ...mapState([
      "user",
      "fbPages",
      "fbPagesLoaded",
      "apiUrl",
      "workingFields",
      "jsonData",
      "fbMappedKeys",
      "isProduction",
      "sendToRegularEndpoint",
      "isErrorJsonData",
    ]),
    canSave() {
      return this.isErrorJsonData;
    },
  },
  data() {
    return {
      loading: true,
      checkingForm: false,
      savingForm: false,
      data: null,
      refreshModal: false,
      refreshOthersModal: false,
      formDisabled: false,
    };
  },
  methods: {
    submitMappings() {
      if (this.postUrl == "") {
        alert("Please fill out the Post URL field.");
        return {};
      }
      if (this.isErrorJsonData || JSON.stringify(this.jsonData) == "") {
        alert("Invalid JSON value");
        return {};
      }
      this.saveForm();
    },
    saveForm() {
      this.savingForm = true;
      const hideLoading = this.$message.loading("Saving");
      axios({
        method: "post",
        url: `${this.apiUrl}/mappings`,
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken,
        },
        params: {
          action: "UPDATE_MAPPING",
          pageId: this.pageId,
          formId: this.formId,
        },
        data: {
          pageName: this.data.PageName,
          formName: this.data.FormName,
          mappings: this.workingFields,
          jsonData: this.jsonData,
          fbMappedKeys: this.fbMappedKeys,
          isProduction: this.isProduction,
          sendToRegularEndpoint: this.sendToRegularEndpoint,
        },
      }).then(
        (rslt) => {
          hideLoading();
          if (rslt.data.status == "ok") {
            this.$router.go(-1);
            alert("Mapping successfully updated.");
          } else {
            alert("There was a problem saving the mapping: " + rslt.data.message);
          }
          this.savingForm = false;
        },
        (err) => {
          console.log(err);
          hideLoading();
          alert("Unable to save the mapping. Server responded with: " + err);
          this.savingForm = false;
        }
      );
    },
    goBack() {
      this.$router.back();
    },
    showRefreshModal() {
      this.refreshModal = true;
    },
    hideRefreshModal() {
      this.refreshModal = false;
    },
    showRefreshOthersModal() {
      this.refreshOthersModal = true;
    },
    hideRefreshOthersModal() {
      this.refreshOthersModal = false;
      this.formDisabled = false;
    },
    connectToFB() {
      this.hideRefreshModal();
      this.formDisabled = true;

      FB.login(
        async (response) => {
          if (response.status !== "connected") {
            alert("You must accept connecting to the application in order to allow subscribing to forms.");
            this.formDisabled = false;
            return;
          }
          // make sure a long-term token is generated for the user
          // on the server so a non-expiring page token can be granted
          // let it just get called then run in the background without
          // checking for anything because it won't be needed until the
          // user has selected a page and a form to work with.
          //
          // this call is asynchronous, no need to wait for it. Worst case scenario
          // it fails and they'll have to try again with FB.
          axios.request({
            method: "post",
            url: this.apiUrl + "/setup-lt-token",
            headers: {
              Authorization: this.user.signInUserSession.idToken.jwtToken,
            },
            params: {
              user_token: response.authResponse.accessToken,
              user_id: response.authResponse.userID,
            },
          });
          // go ahead and load up the pages that the user has authorized to use.
          let hasNext = true;
          let fbUrl = "/me/accounts";
          const fbPages = [];
          // Need to create a function that is async so this can be put in a while
          // loop since the node-style callback will just cause the loop to happen
          // before the callback returns. Basically, wrap it in a function that
          // returns back a promise.
          const fetchPages = async () => {
            return new Promise((resolve, reject) => {
              FB.api(fbUrl, (response) => {
                if (response.err) {
                  alert("Problem getting pages from Facebook for you.");
                  hasNext = false;
                } else {
                  if (response && "data" in response) {
                    fbPages.push(...response.data);
                    if ("paging" in response && "next" in response) {
                      const url = response.paging.next;
                      fbUrl = url.substring(url.indexOf(process.env.VUE_APP_FACEBOOK_API_VERSION) + 5);
                    } else {
                      hasNext = false;
                    }
                  } else {
                    hasNext = false;
                  }
                }
                resolve();
              });
            });
          };
          while (hasNext) {
            await fetchPages();
          }

          this.$store.dispatch("saveFbPages", {
            pages: fbPages,
            setPages: false,
          });

          //Show
          axios
            .request({
              method: "post",
              url: this.apiUrl + "/update-form-subscription-token",
              headers: {
                Authorization: this.user.signInUserSession.idToken.jwtToken,
              },
              params: {
                pageId: this.data.FbPageId,
                formId: this.data.FbFormId,
              },
            })
            .then((result) => {
              this.showRefreshOthersModal();
            })
            .catch((err) => {
              alert(err.response.data.message);
              this.formDisabled = false;
            });
        },
        {
          scope:
            "ads_management,leads_retrieval,pages_manage_ads,pages_manage_metadata,pages_read_engagement,pages_read_user_content,pages_show_list,business_management",
        }
      );
    },
    refreshOtherFormTokens() {
      axios
        .request({
          method: "post",
          url: this.apiUrl + "/update-page-tokens",
          headers: {
            Authorization: this.user.signInUserSession.idToken.jwtToken,
          },
          params: {
            pageId: this.data.FbPageId,
            formId: this.data.FbFormId,
          },
        })
        .then((result) => {
          alert("Refreshing the other tokens of the Facebook Page successful!");
        })
        .catch((err) => {
          alert("Refreshing the other tokens of the Facebook Page failed.");
        })
        .finally(() => {
          this.formDisabled = false;
        });
    },
  },
  mounted() {
    // load up the form data from the database. I suppose we could do this in
    // the store instead.
    axios({
      method: "post",
      url: `${this.apiUrl}/mappings`,
      headers: {
        Authorization: this.user.signInUserSession.idToken.jwtToken,
      },
      params: {
        action: "GET_MAPPING",
        pageId: this.pageId,
        formId: this.formId,
      },
    })
      .then((rslt) => {
        this.data = rslt.data;
        this.$store.commit("setFormData", {
          jsonData: rslt.data.JsonData,
          fbMappedKeys: rslt.data.FBMappedKeys,
          isProduction: rslt.data.IsProduction,
          // If FormSubscription has no defined SendToRegularEndpoint field, we set the value to true as default
          sendToRegularEndpoint: rslt.data.hasOwnProperty("SendToRegularEndpoint")
            ? rslt.data.SendToRegularEndpoint
            : true,
          isError: false,
        });
        this.loading = false;
      })
      .catch((err) => {
        alert("Unable to load form");
        this.loading = false;
      });
  },
  props: ["formId", "pageId"],
};
</script>

<style lang="scss" scoped>
#add-form {
  display: flex;
  flex-direction: column;
}
.create-button {
  margin-right: 10px;
}
</style>
<style>
.center-buttons .ant-form-item-control {
  text-align: center;
}
.form-id {
  color: #999;
  margin-left: 15px;
}
#refresh-button {
  margin-left: 15px;
}
.form-disabled {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1000;
}
</style>
